import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios, { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { MantineColorScheme, useMantineColorScheme } from "@mantine/core";
import { jwtDecode } from "jwt-decode";
import { useRouting } from "stores/routingStore/RoutingContext";
import { User } from "types/app";
import { uninterceptedAxiosInstance } from "utils/axios/axios";
import { getErrorMsg } from "utils/getErrorMsg/getErrorMsg";
import { storeUnits } from "utils/units/getAutomaticUnits";
import {
  parseSettingsToObj,
  parseSettingsToString,
  UserSettings,
} from "utils/helpers";
import { showErrorNotification } from "utils/notifications/customNotifications";
import { useLocalUserData } from "./useLocalUserData";

const BASE_API_URL = import.meta.env.VITE_BASE_API_URL ?? "";

type JwtToken = {
  id: number;
  iat: number;
};

type MessageResponse = {
  msg?: string;
};

type InternalErrorsResponse = {
  internal_errors?: { msg: string; err: string }[];
};

type RegisterConflictResponse = {
  msg_email?: string;
  msg_password?: string;
  msg_password_repeat?: string;
};

type SignInResponse = {
  token: string;
  settings: string;
};

type SignInReturn = {
  user: Omit<User, "id"> | null;
};

type SignUpReturn = {
  success: string | null;
  errors: Record<string, string | undefined> | null;
  error?: string | null;
};

export class UpdateUserMultiError extends Error {
  errorCodes: string[];

  constructor(errorCodes: string[]) {
    super();
    this.errorCodes = errorCodes;
  }
}

export interface Auth {
  user: User | null;
  signIn: (email: string, password: string) => Promise<SignInReturn>;
  update: (settings: Partial<UserSettings>) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    passwordrepeat: string,
    receiveNewsletter: boolean,
    colorScheme?: MantineColorScheme
  ) => Promise<SignUpReturn>;
  resetPassword: (
    email: string
  ) => Promise<{ success: string | null; error: string | null }>;
  setPassword: (
    password: string,
    passwordRepeat: string,
    token: string
  ) => Promise<{ success: string | null; error: string | null }>;
  loading: boolean;
  deleteUser: () => Promise<void>;
  getAppSubscriptionStatus: (language: string) => Promise<
    AxiosResponse<
      {
        errors: string[];
        state: "active" | "inGracePeriod" | "inactive";
      },
      unknown
    >
  >;
  getUserSettings: () => Promise<Partial<UserSettings> | null>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export type PickedAuthInstance = Pick<
  Auth,
  "getAppSubscriptionStatus" | "getUserSettings" | "user" | "signOut" | "update"
>;

const AuthContext = React.createContext<Auth>({
  user: null,
  loading: false,
} as Auth);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({
  children,
  onAuth,
  onSignOut,
}: React.PropsWithChildren<{
  onAuth: (authInstance: PickedAuthInstance) => void;
  onSignOut: () => void;
}>) => {
  const {
    setLocalUser,
    localUser,
    removeLocalUser: removeUser,
  } = useLocalUserData();
  const [user, setUser] = useState<User | null>(localUser);
  // The user's latest settings, saving merges into them. Kept apart from `user`, which stays as it was
  // when the user signed in, so saving a setting doesn't rerun everything that depends on the user.
  const settingsRef = useRef(user?.settings ?? null);
  useEffect(() => {
    settingsRef.current = user?.settings ?? null;
  }, [user]);
  const [loading, setLoading] = useState(false);
  const { state, changeRouting } = useRouting();
  const { i18n } = useTranslation();
  // From i18n, not from the options: this provider is above the OptionsProvider, so useOptions()
  // would only return the defaults. useLanguage keeps both in sync.
  const language = i18n.language;
  const initialized = useRef(false);
  const { clearColorScheme } = useMantineColorScheme();

  const currentTrackWaypointsCount = state?.trackWaypoints?.length;

  const signUp = async (
    email: string,
    password: string,
    passwordrepeat: string,
    receiveNewsletter: boolean,
    colorScheme?: MantineColorScheme
  ) => {
    try {
      setLoading(true);
      const { data } = await uninterceptedAxiosInstance.post<
        MessageResponse & InternalErrorsResponse
      >(
        `${BASE_API_URL}/register`,
        {
          email,
          password,
          passwordrepeat,
          receiveNewsletter,
          colorScheme,
          agent: "Trackbook",
          lng: language,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // This is not the cleanest way how to handle errors that shouldn't block
      // user registration flow but at the time it was the path of least resistance
      if (Array.isArray(data?.internal_errors)) {
        data.internal_errors.forEach((error) => {
          showErrorNotification({
            autoClose: 7000,
            message: error.msg,
          });
        });
      }

      return { success: data.msg ?? null, errors: null, error: null };
    } catch (error) {
      if (axios.isAxiosError<RegisterConflictResponse>(error)) {
        const errors = error?.response?.data;
        if (error.response?.status === 409) {
          return {
            success: null,
            errors: {
              email: errors?.msg_email,
              password: errors?.msg_password,
              passwordrepeat: errors?.msg_password_repeat,
            },
            error: null,
          };
        }
      }
      return { success: null, errors: null, error: getErrorMsg(error) };
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  // Loader is set externally for this call
  const signIn = async (email: string, password: string) => {
    try {
      const res = await uninterceptedAxiosInstance.post<SignInResponse>(
        `${BASE_API_URL}/login`,
        {
          email,
          password,
          agent: "Trackbook",
          lng: language,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      const token = res?.data?.token;
      const settingsString = res?.data?.settings;
      let settings = null;
      try {
        settings = parseSettingsToObj(settingsString);
      } catch (error) {
        console.error(error);
      }
      const tokenData: JwtToken = jwtDecode(token);
      const signedInUser: User = { email, settings, id: tokenData.id };

      setUser(signedInUser);
      setLocalUser(signedInUser);
      return { user: signedInUser };
    } catch (error) {
      setUser(null);
      throw error;
    }
  };

  const update = useCallback(
    async (settings: Partial<UserSettings>) => {
      if (!user) throw new Error("user_not_signed_in");
      setLoading(true);
      const mergedSettings = { ...settingsRef.current, ...settings };
      settingsRef.current = mergedSettings;
      setLocalUser({ ...user, settings: mergedSettings });
      const settingsAsString = parseSettingsToString(mergedSettings);
      try {
        const { data } = await axios.post<InternalErrorsResponse>(
          `${BASE_API_URL}/update-user`,
          {
            agent: "Trackbook",
            lng: language,
            settings: settingsAsString,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        if (
          Array.isArray(data?.internal_errors) &&
          data?.internal_errors.length > 0
        ) {
          const errors = data.internal_errors;

          errors.forEach((error) => {
            showErrorNotification({
              autoClose: 7000,
              message: error.msg,
            });
          });

          throw new UpdateUserMultiError(errors.map((error) => error.err));
        }
      } catch (error) {
        if (error instanceof UpdateUserMultiError) {
          // We just propagate the error here
          throw error;
        } else {
          showErrorNotification({
            autoClose: 7000,
            message: axios.isAxiosError<MessageResponse>(error)
              ? error.response?.data?.msg
              : getErrorMsg(error),
          });

          throw new Error("generic_update_failed", { cause: error });
        }
      } finally {
        setLoading(false);
      }
    },
    [language, setLocalUser, user]
  );

  const signOut = useCallback(async () => {
    removeUser();
    setUser(null);
    initialized.current = false;
    clearColorScheme();

    if (onSignOut) {
      onSignOut();
    }

    // calling logout endpoint will delete the httponly cookie
    try {
      await uninterceptedAxiosInstance.get(`${BASE_API_URL}/logout`, {
        withCredentials: true,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 401) {
          console.warn("The cookie was already deleted");
        }
      }
    }
  }, [clearColorScheme, onSignOut, removeUser]);

  const setPassword = async (
    password: string,
    passwordRepeat: string,
    token: string
  ) => {
    try {
      setLoading(true);
      const res = await uninterceptedAxiosInstance.post<MessageResponse>(
        `${BASE_API_URL}/set-password`,
        {
          passwordreset: token,
          password,
          passwordrepeat: passwordRepeat,
          agent: "Trackbook",
          lng: language,
        }
      );
      return { success: res.data?.msg ?? null, error: null };
    } catch (error) {
      if (axios.isAxiosError<MessageResponse>(error)) {
        return { success: null, error: error.response?.data?.msg ?? null };
      }
      return { success: null, error: getErrorMsg(error) };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const res = await uninterceptedAxiosInstance.post<MessageResponse>(
        `${BASE_API_URL}/password-reset`,
        {
          email,
          agent: "Trackbook",
          lng: language,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return { success: res.data?.msg ?? null, error: null };
    } catch (error) {
      if (axios.isAxiosError<MessageResponse>(error)) {
        return { success: null, error: error.response?.data?.msg ?? null };
      }
      return { success: null, error: getErrorMsg(error) };
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = useCallback(async () => {
    if (!user) throw new Error("user_not_signed_in");
    setLoading(true);
    await axios.post(
      `${BASE_API_URL}/delete-user`,
      {
        agent: "Trackbook",
        lng: language,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    await signOut();
    setLoading(false);
  }, [language, signOut, user]);

  const getAppSubscriptionStatus = useCallback(async (language: string) => {
    return uninterceptedAxiosInstance.post<{
      errors: string[];
      state: "active" | "inGracePeriod" | "inactive";
    }>(
      `${BASE_API_URL}/get-app-subscription-status`,
      {
        agent: "Trackbook",
        lng: language,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
  }, []);

  const getUserSettings = useCallback(async () => {
    const { data } = await uninterceptedAxiosInstance.post<{
      settings: string;
    }>(
      `${BASE_API_URL}/get-user-settings`,
      {
        agent: "Trackbook",
        lng: language,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    // The same parsing as on sign in
    const settings = parseSettingsToObj(data.settings);

    // The server's settings are the current ones, so saving a setting later merges into them and not into the
    // settings stored in this browser, which can be older (e.g. changed on another device since)
    if (settings) {
      const mergedSettings = { ...settingsRef.current, ...settings };
      settingsRef.current = mergedSettings;
      setLocalUser((localUser) =>
        localUser ? { ...localUser, settings: mergedSettings } : localUser
      );
    }

    if (settings?.language) {
      void i18n.changeLanguage(settings?.language);
      localStorage.setItem("language", settings?.language);
    }
    // Like the language, the account's units stay in this browser
    storeUnits(settings?.units);

    return settings;
  }, [i18n, language, setLocalUser]);

  useEffect(() => {
    if (
      user?.settings?.routing &&
      currentTrackWaypointsCount === 0 &&
      initialized.current === false
    ) {
      changeRouting(user.settings.routing);
    }

    if (onAuth) {
      onAuth({
        getAppSubscriptionStatus,
        getUserSettings,
        signOut,
        update,
        user,
      });
    }

    initialized.current = true;
  }, [
    currentTrackWaypointsCount,
    changeRouting,
    getAppSubscriptionStatus,
    getUserSettings,
    onAuth,
    signOut,
    update,
    user,
  ]);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        update,
        setLoading,
        signOut,
        signUp,
        resetPassword,
        loading,
        deleteUser,
        setPassword,
        getAppSubscriptionStatus,
        getUserSettings,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
